"use client";

import { useState, useRef, useEffect } from "react";
import type { CSSProperties } from "react";
import { useRouter } from "next/navigation";
import { useLocale, useTranslations } from "next-intl";
import { User, LogOut, ChevronDown } from "lucide-react";
import { Link } from "@/i18n/navigation";
import { useAuth } from "@/contexts/auth-context";
import { cn } from "@/lib/utils";

export function NavbarAuthButton() {
  const t = useTranslations("navigation");
  const locale = useLocale();
  const router = useRouter();
  const { user, isAuthenticated, isLoading, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState<CSSProperties>({});
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const updateMenuPosition = () => {
      if (!ref.current) return;

      const rect = ref.current.getBoundingClientRect();
      const menuWidth = 208;
      const viewportPadding = 8;
      const top = rect.bottom + 8;
      const preferredLeft = rect.right - menuWidth;
      const left = Math.min(
        Math.max(preferredLeft, viewportPadding),
        window.innerWidth - menuWidth - viewportPadding,
      );

      setMenuStyle({ top, left, width: menuWidth });
    };

    updateMenuPosition();
    window.addEventListener("resize", updateMenuPosition);
    window.addEventListener("scroll", updateMenuPosition, true);

    return () => {
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", updateMenuPosition, true);
    };
  }, [open]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const handleLogout = async () => {
    setOpen(false);
    await logout();
    router.replace(locale === "en" ? "/en/auth" : "/auth");
  };

  if (isLoading) return null;

  if (!isAuthenticated || !user) {
    return (
      <Link
        href="/auth"
        className="flex items-center gap-1.5 rounded-lg sm:rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-2.5 sm:px-3.5 py-1.5 sm:py-2 text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-gray-300 dark:hover:border-gray-600 transition-all shadow-sm"
      >
        <User className="hidden sm:block h-4 w-4" />
        <span>{t("signIn")}</span>
      </Link>
    );
  }

  const displayName =
    [user.name, user.last_name].filter(Boolean).join(" ") || user.username;

  const initials = displayName
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("") || "U";

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        aria-haspopup="true"
        className="flex items-center gap-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 pl-1.5 pr-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all shadow-sm"
      >
        <div className="h-7 w-7 rounded-full bg-gradient-to-br from-brand-red to-brand-red-deep flex items-center justify-center text-white text-xs font-bold">
          {initials}
        </div>
        <span className="max-w-[100px] truncate hidden sm:block">{displayName}</span>
        <ChevronDown
          className={cn("h-3.5 w-3.5 text-gray-400 transition-transform duration-200", open && "rotate-180")}
        />
      </button>

      {open && (
        <div
          className="fixed rounded-2xl border border-gray-100 dark:border-gray-700/60 bg-white dark:bg-dark shadow-lg shadow-black/10 dark:shadow-black/30 overflow-hidden z-[70]"
          style={menuStyle}
        >
          <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700/60">
            <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{displayName}</p>
            <p className="text-xs text-gray-400 dark:text-gray-500 truncate mt-0.5">{user.email}</p>
          </div>
          <div className="p-1.5">
            <Link
              href="/perfil"
              onClick={() => setOpen(false)}
              className="flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              <User className="h-4 w-4 text-gray-400" />
              {t("profile")}
            </Link>
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
            >
              <LogOut className="h-4 w-4" />
              {t("logout")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
